/**
 * MultiplayerGameView — Modern visual game board for multiplayer.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGameStore, useAuthStore, useRoomStore } from '../stores';
import { emitCall, emitPlayCard, emitLeaveRoom } from '../hooks/useSocket';
import { useSoundEffects } from '../hooks/useSoundEffects';
import {
  getLocalPlayer,
  getTurnInfo,
  getPlayableCardInfos,
  getValidCallValues,
  getRelativePlayerOrder,
} from '@callbridge/ui';
import { Card, CardBack } from './Card';

interface Props {
  roomCode: string;
}

const SEAT_MAP = ['bottom', 'left', 'top', 'right'] as const;

export function MultiplayerGameView({ roomCode }: Props) {
  const navigate = useNavigate();
  const game = useGameStore();
  const user = useAuthStore((s) => s.user);
  const roomLeft = useRoomStore((s) => s.left);
  const { play: playSound } = useSoundEffects();
  const prevPhaseRef = useRef(game.phase);

  const localPlayer = getLocalPlayer(game);
  const turnInfo = getTurnInfo(game);
  const playableCards = getPlayableCardInfos(game.hand, game.leadSuit, game.trickCards);
  const validCalls = getValidCallValues();
  const playerOrder = getRelativePlayerOrder(game);
  const [collectDirection, setCollectDirection] = useState<(typeof SEAT_MAP)[number] | null>(null);
  const [showScoreboard, setShowScoreboard] = useState(false);

  // When a trick is won, pause briefly to show the winning trick, then slide
  // the played cards toward the winner's seat before clearing the table.
  useEffect(() => {
    if (!game.trickWinnerId) {
      setCollectDirection(null);
      return;
    }

    const relIdx = playerOrder.findIndex((p) => p.id === game.trickWinnerId);
    const seat = SEAT_MAP[relIdx] ?? 'bottom';

    const collectTimer = setTimeout(() => setCollectDirection(seat), 900);
    const clearTimer = setTimeout(() => {
      game.trickCleared();
      setCollectDirection(null);
    }, 1400);

    return () => {
      clearTimeout(collectTimer);
      clearTimeout(clearTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game.trickWinnerId]);

  useEffect(() => {
    if (game.phase === prevPhaseRef.current) return;
    prevPhaseRef.current = game.phase;
    if (game.phase === 'playing' && turnInfo.isMyTurn) playSound('yourTurn');
    if (game.phase === 'gameOver') playSound('gameOver');
    if (game.phase === 'roundEnd') playSound('trickWon');
  }, [game.phase, turnInfo.isMyTurn, playSound]);

  const handleCall = useCallback(
    async (call: number) => {
      playSound('callMade');
      try {
        await emitCall(call);
      } catch (err) {
        game.errorSet(err instanceof Error ? err.message : 'Failed to make call');
      }
    },
    [game, playSound],
  );

  const handlePlayCard = useCallback(
    async (suit: string, rank: number) => {
      playSound('cardPlay');
      try {
        await emitPlayCard(suit, rank);
      } catch (err) {
        game.errorSet(err instanceof Error ? err.message : 'Failed to play card');
      }
    },
    [game, playSound],
  );

  const handleLeaveGame = useCallback(async () => {
    try {
      await emitLeaveRoom();
    } catch {
      /* best effort */
    }
    game.reset();
    roomLeft();
    navigate('/play/multiplayer');
  }, [game, roomLeft, navigate]);

  const handleContinueRound = useCallback(() => {
    game.trickCleared();
  }, [game]);

  if (game.phase === 'idle' || !game.gameId) {
    return (
      <div className="game-board">
        <div className="game-topbar">
          <button
            className="topbar-btn"
            onClick={handleLeaveGame}
            title="Leave"
            aria-label="Leave game"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="topbar-room">Room: {roomCode}</span>
        </div>
        <div className="felt-table">
          <div className="trick-center" role="status" aria-live="polite">
            <div className="waiting-dots">
              <span />
              <span />
              <span />
            </div>
            <span className="trick-empty-text">Waiting for game to start...</span>
          </div>
        </div>
      </div>
    );
  }

  const currentTurnId = game.players[game.currentTurnIndex]?.id;

  return (
    <div className="game-board">
      {/* Top Bar */}
      <div className="game-topbar">
        <button
          className="topbar-btn"
          onClick={handleLeaveGame}
          title="Leave"
          aria-label="Leave game"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="topbar-center">
          <span className="topbar-round">Round {game.roundNumber}</span>
          <span className="topbar-divider" />
          <span className="topbar-phase">
            {game.phase === 'playing'
              ? 'Playing'
              : game.phase === 'calling'
                ? 'Calling'
                : game.phase === 'roundEnd'
                  ? 'Round End'
                  : game.phase}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            className="topbar-btn"
            onClick={() => setShowScoreboard((v) => !v)}
            title="Score table"
            aria-label="Show score table"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <path d="M3 9h18M9 21V9" />
            </svg>
          </button>
          <span className="topbar-room">{roomCode}</span>
        </div>
      </div>

      {game.error && (
        <div className="game-toast game-toast-error" role="alert">
          {game.error}
          <button className="toast-dismiss" onClick={() => game.errorSet(null)}>
            ×
          </button>
        </div>
      )}

      {/* Felt Table */}
      <div className="felt-table">
        {/* Opponents (not bottom/local) */}
        {playerOrder.map((p, relIdx) => {
          const seat = SEAT_MAP[relIdx] ?? 'top';
          if (seat === 'bottom') return null; // local player rendered in hand dock
          const isTurn = p.id === currentTurnId;
          return (
            <div
              key={p.id}
              className={`opponent-seat opponent-${seat} ${isTurn ? 'is-turn' : ''} ${!p.isConnected ? 'is-disconnected' : ''}`}
            >
              <div className="opponent-cards">
                {Array.from({ length: Math.min(game.hand.length || 5, 13) }).map((_, i) => (
                  <CardBack
                    key={i}
                    size="sm"
                    className="opponent-card-back"
                    style={
                      {
                        '--fan-index': i,
                        '--fan-total': Math.min(game.hand.length || 5, 13),
                      } as React.CSSProperties
                    }
                  />
                ))}
              </div>
              <div className="opponent-info">
                <div
                  className={`opponent-avatar ${isTurn ? 'avatar-glow' : ''} ${p.isAI ? 'avatar-ai' : ''}`}
                >
                  {p.isAI ? '🤖' : p.name.charAt(0).toUpperCase()}
                </div>
                <span className="opponent-name">
                  {p.name}
                  {!p.isConnected && <span className="disconnect-dot" />}
                </span>
                <span className="opponent-score">
                  {p.call ?? '-'}/{p.tricksWon}
                  <span className="opponent-total">{p.totalScore}pt</span>
                </span>
              </div>
              {isTurn && (
                <div className="turn-timer-ring">
                  <svg viewBox="0 0 36 36">
                    <circle
                      cx="18"
                      cy="18"
                      r="16"
                      fill="none"
                      stroke="rgba(255,255,255,0.1)"
                      strokeWidth="2"
                    />
                    <circle
                      cx="18"
                      cy="18"
                      r="16"
                      fill="none"
                      stroke="var(--color-accent)"
                      strokeWidth="2"
                      strokeDasharray="100"
                      strokeDashoffset={100 - turnInfo.timeRemainingPct}
                      strokeLinecap="round"
                      transform="rotate(-90 18 18)"
                    />
                  </svg>
                </div>
              )}
            </div>
          );
        })}

        {/* Center Trick Area */}
        <div className="trick-center">
          {game.trickCards.length === 0 &&
            (game.phase === 'playing' || game.phase === 'calling') && (
              <span className="trick-empty-text">
                {turnInfo.isMyTurn ? 'Your Turn' : `${turnInfo.currentPlayer?.name}'s Turn`}
              </span>
            )}
          <div
            className={`trick-played-cards ${collectDirection ? `trick-collecting trick-collect-${collectDirection}` : ''}`}
          >
            {game.trickCards.map((tc, idx) => (
              <div
                key={`${tc.card.suit}-${tc.card.rank}`}
                className="trick-played-card"
                style={
                  {
                    '--trick-index': idx,
                    '--trick-total': game.trickCards.length,
                  } as React.CSSProperties
                }
              >
                <Card suit={tc.card.suit} rank={tc.card.rank} size="lg" />
              </div>
            ))}
          </div>
          {game.trickWinnerId && (
            <div className="trick-winner-badge anim-fade-in">
              {game.players.find((p) => p.id === game.trickWinnerId)?.name} wins!
            </div>
          )}
        </div>
      </div>

      {/* Call Panel */}
      {game.phase === 'calling' && turnInfo.isMyTurn && (
        <div
          className="call-overlay anim-slide-up"
          role="dialog"
          aria-modal="true"
          aria-labelledby="mp-call-title"
        >
          <div className="call-glass">
            <h3 id="mp-call-title" className="call-title">
              Make Your Call
            </h3>
            <div className="call-chips">
              {validCalls.map((call) => (
                <button key={call} className="call-chip" onClick={() => handleCall(call)}>
                  {call}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Player Hand */}
      <div className="hand-dock">
        <div className="hand-player-info">
          <div className={`hand-avatar ${turnInfo.isMyTurn ? 'avatar-glow' : ''}`}>
            {localPlayer?.name?.charAt(0).toUpperCase() ?? 'Y'}
          </div>
          <span className="hand-player-name">{localPlayer?.name ?? 'You'}</span>
          <span className="hand-score">
            {localPlayer?.call ?? '-'}/{localPlayer?.tricksWon ?? 0} &middot;{' '}
            {localPlayer?.totalScore ?? 0} pts
          </span>
        </div>
        <div className="hand-fan">
          {playableCards.map((ci, idx) => (
            <Card
              key={`${ci.card.suit}-${ci.card.rank}`}
              suit={ci.card.suit}
              rank={ci.card.rank}
              size="lg"
              playable={ci.isPlayable && turnInfo.isMyTurn && game.phase === 'playing'}
              disabled={!ci.isPlayable || !turnInfo.isMyTurn || game.phase !== 'playing'}
              onClick={() => {
                if (ci.isPlayable && turnInfo.isMyTurn && game.phase === 'playing') {
                  handlePlayCard(ci.card.suit, ci.card.rank);
                }
              }}
              className="hand-fan-card"
              style={
                {
                  '--fan-index': idx,
                  '--fan-total': playableCards.length,
                } as React.CSSProperties
              }
            />
          ))}
        </div>
      </div>

      {/* Waiting for other player */}
      {(game.phase === 'calling' || game.phase === 'playing') && !turnInfo.isMyTurn && (
        <div className="turn-waiting">
          <div className="waiting-dots">
            <span />
            <span />
            <span />
          </div>
          Waiting for {turnInfo.currentPlayer?.name}...
          {turnInfo.isLowTime && <span className="low-time-pulse"> Low time!</span>}
        </div>
      )}

      {/* Round End */}
      {game.phase === 'roundEnd' && game.roundResults && (
        <div className="result-overlay">
          <div className="result-glass anim-slide-up">
            <h2 className="result-title">Round {game.roundNumber} Complete</h2>
            <table className="round-table">
              <thead>
                <tr>
                  <th>Player</th>
                  <th>Call</th>
                  <th>Tricks</th>
                  <th>Score</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {game.roundResults.map((r) => {
                  const player = game.players.find((p) => p.id === r.playerId);
                  const met = r.tricksWon >= r.call;
                  return (
                    <tr key={r.playerId} className={met ? 'row-met' : 'row-missed'}>
                      <td>{player?.name ?? r.playerId}</td>
                      <td>{r.call}</td>
                      <td>{r.tricksWon}</td>
                      <td className={r.roundScore >= 0 ? 'score-pos' : 'score-neg'}>
                        {r.roundScore > 0 ? '+' : ''}
                        {r.roundScore}
                      </td>
                      <td>{r.totalScore}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <button className="btn-game btn-game-primary" onClick={handleContinueRound}>
              Continue
            </button>
          </div>
        </div>
      )}

      {/* Game Over */}
      {game.phase === 'gameOver' && game.winnerId && game.finalScores && (
        <div className="result-overlay">
          <div className="result-glass anim-celebrate">
            <h2 className="result-title">
              {game.winnerId === user?.id
                ? 'Victory!'
                : `${game.players.find((p) => p.id === game.winnerId)?.name} Wins`}
            </h2>
            <div className="result-trophy">{game.winnerId === user?.id ? '🏆' : '🎮'}</div>
            <div className="result-scores">
              {Object.entries(game.finalScores)
                .sort(([, a], [, b]) => b - a)
                .map(([pid, score], idx) => (
                  <div
                    key={pid}
                    className={`result-row ${pid === game.winnerId ? 'result-winner' : ''}`}
                  >
                    <span className="result-rank">#{idx + 1}</span>
                    <span className="result-name">
                      {game.players.find((p) => p.id === pid)?.name ?? pid}
                    </span>
                    <span className="result-pts">{score} pts</span>
                  </div>
                ))}
            </div>
            <div className="result-actions">
              <button className="btn-game btn-game-primary" onClick={handleLeaveGame}>
                Back to Lobby
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scoreboard overlay */}
      {showScoreboard && (
        <div className="result-overlay" onClick={() => setShowScoreboard(false)}>
          <div className="result-glass scoreboard-glass anim-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="scoreboard-header">
              <h2 className="result-title" style={{ margin: 0 }}>Score Table</h2>
              <button
                className="topbar-btn"
                onClick={() => setShowScoreboard(false)}
                aria-label="Close"
                style={{ flexShrink: 0 }}
              >
                ×
              </button>
            </div>
            <table className="round-table scoreboard-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th style={{ textAlign: 'left' }}>Player</th>
                  <th>Total</th>
                  <th>Call</th>
                  <th>Tricks</th>
                </tr>
              </thead>
              <tbody>
                {[...game.players]
                  .sort((a, b) => b.totalScore - a.totalScore)
                  .map((p, idx) => {
                    const isLocal = p.id === game.players[game.localPlayerIndex]?.id;
                    return (
                      <tr key={p.id} className={isLocal ? 'row-local' : ''}>
                        <td className="sb-rank">#{idx + 1}</td>
                        <td className="sb-name">
                          {p.isAI ? '🤖 ' : ''}{p.name}
                          {isLocal && <span className="sb-you"> (You)</span>}
                          {!p.isConnected && <span className="disconnect-dot" style={{ marginLeft: 4 }} />}
                        </td>
                        <td className="sb-total score-pos">{p.totalScore}</td>
                        <td>{p.call ?? '—'}</td>
                        <td>{p.tricksWon}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
            {game.roundHistory.length > 0 && (
              <>
                <p className="sb-history-label">Round History</p>
                <div className="sb-history">
                  {game.roundHistory.map((round, ri) => (
                    <div key={ri} className="sb-history-round">
                      <span className="sb-history-rnum">R{ri + 1}</span>
                      {round
                        .slice()
                        .sort((a, b) => b.roundScore - a.roundScore)
                        .map((r) => {
                          const pname = game.players.find((p) => p.id === r.playerId)?.name ?? r.playerId;
                          return (
                            <span key={r.playerId} className="sb-history-entry">
                              {pname}: {r.roundScore > 0 ? '+' : ''}{r.roundScore}
                            </span>
                          );
                        })}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Disconnected players banner */}
      {game.players.some((p) => !p.isConnected) && (
        <div className="disconnect-bar">
          {game.players
            .filter((p) => !p.isConnected)
            .map((p) => p.name)
            .join(', ')}{' '}
          disconnected
        </div>
      )}
    </div>
  );
}
